<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";
import DRMenu from "@/components/UI/menu/DRMenu.vue";
import { MenuType, type MenuEntity } from "@/components/UI/menu/menu.types";
import { useScreen } from "@/composable/useResponsive";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const { t } = useTranslation();
const { screen } = useScreen();
const route = useRoute();
const router = useRouter();

const settingsMenu: MenuEntity[] = [
  {
    type: MenuType.GROUP,
    label: "dashboard:navigation.administration",
    children: [
      {
        type: MenuType.LINK,
        icon: "users",
        label: "dashboard:navigation.members",
        route: { name: "settings-members" },
        index: "settings-members",
      },
    ],
  },
  {
    type: MenuType.GROUP,
    label: "dashboard:navigation.referentiels",
    children: [
      {
        type: MenuType.LINK,
        icon: "palette",
        label: "dashboard:navigation.legends",
        route: { name: "settings-legends" },
        index: "settings-legends",
      },
      {
        type: MenuType.LINK,
        icon: "file-text",
        label: "dashboard:navigation.report-templates",
        route: { name: "settings-templates" },
        index: "settings-templates",
      },
      {
        type: MenuType.LINK,
        icon: "tree-structure",
        label: "dashboard:navigation.structure-parts",
        route: { name: "settings-part-templates" },
        index: "settings-part-templates",
      },
    ],
  },
];

// Flat list of tabs for mobile view
const tabs = computed(() => {
  const items: { icon: string; label: string; routeName: string }[] = [];
  for (const group of settingsMenu) {
    if (group.children) {
      for (const child of group.children) {
        if (
          child.route &&
          typeof child.route === "object" &&
          "name" in child.route
        ) {
          items.push({
            icon: child.icon || "",
            label: child.label,
            routeName: child.route.name as string,
          });
        }
      }
    }
  }
  return items;
});

const activeTab = computed(() => route.name as string);

const onTabClick = (routeName: string) => {
  router.push({ name: routeName });
};
</script>

<template>
  <div class="settings-layout d-flex flex-column">
    <!-- Tabs (when main nav is in minimal mode) -->
    <div v-if="screen.lt.lg" class="settings-tabs border-bottom">
      <el-scrollbar>
        <div class="d-flex">
          <button
            v-for="tab in tabs"
            :key="tab.routeName"
            class="settings-tab"
            :class="{ active: activeTab === tab.routeName }"
            @click="onTabClick(tab.routeName)"
          >
            <PhosphorIcon :name="tab.icon" :size="18" />
            <span class="tab-label">{{ t(tab.label) }}</span>
          </button>
        </div>
      </el-scrollbar>
    </div>

    <div class="d-flex flex-grow-1">
      <!-- Sidebar (when main nav is expanded) -->
      <div v-if="!screen.lt.lg" class="settings-sidebar border-end">
        <DRMenu :menu="settingsMenu" type="params" class="p-2" />
      </div>

      <div class="settings-main flex-grow-1">
        <RouterView />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.settings-layout {
  background-color: var(--bs-body-bg);
  min-height: calc(100vh - 50px);
}

.settings-tabs {
  flex-shrink: 0;
  background-color: var(--bs-body-bg);
  padding: 0 0.5rem;
}

.settings-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: var(--bs-secondary-color);
  font-size: 0.875rem;
  white-space: nowrap;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--bs-body-color);
  }

  &.active {
    color: var(--bs-primary);
    border-bottom-color: var(--bs-primary);
  }
}

.tab-label {
  font-weight: 500;
}

.settings-sidebar {
  width: 15rem;
  flex-shrink: 0;
  background-color: var(--bs-body-bg);
  position: sticky;
  top: 0;
  height: calc(100vh - 50px);
  overflow-y: auto;
}

.settings-main {
  padding: 0;
  flex-grow: 1;
  overflow: hidden;
}
</style>
